/**
 * MSAL Authentication Service
 *
 * Centralized service for handling Microsoft Authentication Library (MSAL) operations
 * including login, logout, token acquisition, and automatic token refresh.
 *
 * Features:
 * - Singleton MSAL instance management
 * - Silent token acquisition (automatic refresh)
 * - Token expiration checking
 * - Role-based access validation
 * - Automatic fallback to interactive login
 */

import { PublicClientApplication } from "@azure/msal-browser";
import { jwtDecode } from "jwt-decode";

// MSAL Configuration
const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_MSAL_APPROVAL_CLIENT_ID,
        authority: process.env.REACT_APP_MSAL_APPROVAL_AUTHORITY,
        redirectUri: process.env.REACT_APP_MSAL_APPROVAL_REDIRECT_URI,
        navigateToLoginRequestUrl: true,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
    },
};

// Login request scopes
const loginRequest = {
    // scopes: ['473db494-352b-43ba-b289-70fb5888b3cf/read']
    scopes: ['db83964f-16e2-4ae8-8631-5acf1af76069/Users.read']
};

// Singleton MSAL instance
let msalInstance = null;

/**
 * Initialize MSAL instance (singleton pattern)
 * @returns {Promise<PublicClientApplication>}
 */
const getMsalInstance = async () => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
    }
    return msalInstance;
};

/**
 * Get MSAL configuration
 * @returns {Object} MSAL configuration object
 */
export const getMsalConfig = () => {
    return msalConfig;
};

/**
 * Check if a token is expired or about to expire
 * @param {string} token - JWT token to check
 * @param {number} bufferSeconds - Buffer time in seconds (default: 300 = 5 minutes)
 * @returns {boolean} True if token is expired or about to expire
 */
export const isTokenExpired = (token, bufferSeconds = 300) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        const expirationTime = decoded.exp;

        // Return true if token is expired or will expire within buffer time
        return expirationTime - currentTime < bufferSeconds;
    } catch (error) {
        console.error('Error decoding token:', error);
        return true;
    }
};

/**
 * Extract roles from JWT token
 * @param {string} token - JWT access token
 * @returns {Array<string>} Array of user roles
 */
export const extractRoles = (token) => {
    try {
        const decoded = jwtDecode(token);
        return decoded.roles || [];
    } catch (error) {
        console.error('Error extracting roles from token:', error);
        return [];
    }
};

/**
 * Validate if user has required roles
 * @param {Array<string>} roles - User roles array
 * @returns {boolean} True if user has at least one role
 */
export const validateRoles = (roles) => {
    return roles && Array.isArray(roles) && roles.length > 0;
};

/**
 * Store authentication data in localStorage
 * @param {Object} authResponse - MSAL authentication response
 * @param {Array<string>} roles - User roles
 */
const storeAuthData = (authResponse, roles) => {
    localStorage.setItem("accessToken", authResponse.accessToken);
    localStorage.setItem("account", JSON.stringify(authResponse.account));
    localStorage.setItem("idToken", authResponse.account.idToken);
    localStorage.setItem("name", authResponse.account.name);
    localStorage.setItem("username", authResponse.account.username);
    localStorage.setItem("roles", JSON.stringify(roles));
    localStorage.setItem("isLoggedin", 'true');
    localStorage.setItem("msalConfig", JSON.stringify(msalConfig));

    // Store token expiration time for tracking
    const decoded = jwtDecode(authResponse.accessToken);
    localStorage.setItem("tokenExpiration", decoded.exp);
};

/**
 * Login with popup
 * @returns {Promise<{success: boolean, roles: Array<string>, error: string}>}
 */
export const loginWithPopup = async () => {
    try {
        const instance = await getMsalInstance();

        // Check if user is already logged in
        const currentAccounts = instance.getAllAccounts();

        if (currentAccounts && currentAccounts.length === 1) {
            // User already logged in, try to acquire token silently
            const account = currentAccounts[0];
            try {
                const silentResponse = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: account
                });

                const roles = extractRoles(silentResponse.accessToken);

                if (!validateRoles(roles)) {
                    return {
                        success: false,
                        roles: [],
                        error: 'You are not authorized! Kindly contact the admin to proceed.'
                    };
                }

                storeAuthData(silentResponse, roles);

                return {
                    success: true,
                    roles: roles,
                    error: null
                };
            } catch (silentError) {
                console.log('Silent token acquisition failed, falling back to popup');
                // Fall through to popup login
            }
        } else if (currentAccounts && currentAccounts.length > 1) {
            console.warn("Multiple accounts detected. Please implement account selection logic.");
        }

        // Perform popup login
        const loginResponse = await instance.loginPopup(loginRequest);

        // Extract and validate roles
        const roles = extractRoles(loginResponse.accessToken);

        if (!validateRoles(roles)) {
            return {
                success: false,
                roles: [],
                error: 'You are not authorized! Kindly contact the admin to proceed.'
            };
        }

        // Store authentication data
        storeAuthData(loginResponse, roles);

        return {
            success: true,
            roles: roles,
            error: null
        };

    } catch (error) {
        console.error("Login Error:", error);
        return {
            success: false,
            roles: [],
            error: error.message || 'Login failed. Please try again.'
        };
    }
};

/**
 * Acquire token silently (automatic refresh)
 * Falls back to interactive login if silent acquisition fails
 * @returns {Promise<string|null>} Access token or null if failed
 */
export const acquireTokenSilent = async () => {
    try {
        const instance = await getMsalInstance();
        const currentAccounts = instance.getAllAccounts();

        if (!currentAccounts || currentAccounts.length === 0) {
            console.log('No accounts found. User needs to login.');
            return null;
        }

        const account = currentAccounts[0];

        try {
            // Try to acquire token silently
            const silentResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: account,
                forceRefresh: false // Use cached token if valid
            });

            // Extract and validate roles
            const roles = extractRoles(silentResponse.accessToken);

            if (!validateRoles(roles)) {
                console.error('User does not have required roles');
                return null;
            }

            // Update stored token
            storeAuthData(silentResponse, roles);

            return silentResponse.accessToken;

        } catch (silentError) {
            console.log('Silent token acquisition failed:', silentError);

            // If silent acquisition fails, try interactive popup
            if (silentError.name === 'InteractionRequiredAuthError') {
                try {
                    const interactiveResponse = await instance.acquireTokenPopup({
                        ...loginRequest,
                        account: account
                    });

                    const roles = extractRoles(interactiveResponse.accessToken);

                    if (!validateRoles(roles)) {
                        console.error('User does not have required roles');
                        return null;
                    }

                    storeAuthData(interactiveResponse, roles);

                    return interactiveResponse.accessToken;
                } catch (interactiveError) {
                    console.error('Interactive token acquisition failed:', interactiveError);
                    return null;
                }
            }

            return null;
        }

    } catch (error) {
        console.error('Error acquiring token:', error);
        return null;
    }
};

/**
 * Get valid access token (returns current if valid, refreshes if expired)
 * @returns {Promise<string|null>} Valid access token or null
 */
export const getValidAccessToken = async () => {
    const currentToken = localStorage.getItem('accessToken');

    // Check if current token is still valid
    if (currentToken && !isTokenExpired(currentToken)) {
        return currentToken;
    }

    // Token expired or doesn't exist, acquire new one
    console.log('Token expired or missing, acquiring new token...');
    return await acquireTokenSilent();
};

/**
 * Logout user
 * @param {string} userName - Username for logout
 * @param {Function} navigate - React Router navigate function
 * @returns {Promise<void>}
 */
export const logout = async (userName, navigate) => {
    try {
        const instance = await getMsalInstance();

        const logoutRequest = {
            account: instance.getAccountByHomeId(userName) || instance.getAllAccounts().find(account => account.username === userName),
            mainWindowRedirectUri: msalConfig.auth.redirectUri,
        };

        // Clear localStorage
        localStorage.clear();
        localStorage.setItem('isLoggedin', 'false');

        // Perform MSAL logout
        await instance.logoutPopup(logoutRequest);

        // Navigate to login page
        if (navigate) {
            navigate('/approval');
        }

    } catch (error) {
        console.error('Logout error:', error);

        // Clear localStorage even if MSAL logout fails
        localStorage.clear();
        localStorage.setItem('isLoggedin', 'false');

        if (navigate) {
            navigate('/approval');
        }
    }
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated with valid token
 */
export const isAuthenticated = () => {
    const token = localStorage.getItem('accessToken');
    const isLoggedIn = localStorage.getItem('isLoggedin') === 'true';

    return isLoggedIn && token && !isTokenExpired(token);
};

/**
 * Get current user's roles
 * @returns {Array<string>} User roles
 */
export const getCurrentUserRoles = () => {
    try {
        const roles = localStorage.getItem('roles');
        return roles ? JSON.parse(roles) : [];
    } catch (error) {
        console.error('Error getting user roles:', error);
        return [];
    }
};

/**
 * Get current account information
 * @returns {Object|null} Account object or null
 */
export const getCurrentAccount = () => {
    try {
        const account = localStorage.getItem('account');
        return account ? JSON.parse(account) : null;
    } catch (error) {
        console.error('Error getting account:', error);
        return null;
    }
};

export default {
    loginWithPopup,
    acquireTokenSilent,
    getValidAccessToken,
    logout,
    isAuthenticated,
    isTokenExpired,
    extractRoles,
    validateRoles,
    getCurrentUserRoles,
    getCurrentAccount,
    getMsalConfig
};